// client/src/components/admin/UserJourneySankey.js
import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import {
  Sankey,
  Tooltip,
  ResponsiveContainer,
  Layer,
  Rectangle,
} from 'recharts'

const MyCustomNode = ({ x, y, width, height, index, payload }) => {
  return (
    <Layer key={`custom-node-${index}`}>
      <Rectangle
        x={x}
        y={y}
        width={width}
        height={height}
        fill="var(--fl-analytics-node-fill)"
        fillOpacity="0.8"
      />
      <text
        x={x + width + 6}
        y={y + height / 2}
        dy="0.35em"
        textAnchor="start"
        fill="var(--fl-analytics-node-text)"
        fontSize={12}
      >
        {payload.name} {payload.value ? `(${payload.value})` : ''}
      </text>
    </Layer>
  )
}

MyCustomNode.propTypes = {
  x: PropTypes.number,
  y: PropTypes.number,
  width: PropTypes.number,
  height: PropTypes.number,
  index: PropTypes.number,
  payload: PropTypes.object,
}

/**
 * UserJourneySankey Component
 *
 * Renders a Sankey diagram visualization of user navigation flows.
 * Shows how users move between different pages/features in the application.
 *
 * @param {Object} props - Component props
 * @param {Object} props.data - Sankey diagram data
 * @param {Array} props.data.nodes - Array of node objects with 'name' property
 * @param {Array} props.data.links - Array of link objects with 'source', 'target', 'value' properties
 */
function UserJourneySankey({ data = null }) {
  // Tiny offset added to duplicate link values to ensure unique React keys
  // while preserving visual appearance (imperceptible to users)
  const DUPLICATE_LINK_OFFSET = 0.0001

  // Process data to add colors to links based on gender
  // Also ensure unique keys by adding tiny fractional values to prevent React warnings
  const processedData = useMemo(() => {
    if (!data) return null

    /**
     * Determines the color for a link based on gender.
     * Uses CSS custom properties from the active theme.
     * @param {string|null|undefined} gender - Gender identifier
     * @returns {string} CSS color value from theme
     */
    const getGenderColor = (gender) => {
      const root = document.documentElement
      if (!gender) {
        return getComputedStyle(root)
          .getPropertyValue('--fl-analytics-gender-neutral')
          .trim()
      }
      const g = gender.toLowerCase()
      if (['male', 'm', 'man'].includes(g)) {
        return getComputedStyle(root)
          .getPropertyValue('--fl-analytics-gender-male')
          .trim()
      }
      if (['female', 'f', 'woman'].includes(g)) {
        return getComputedStyle(root)
          .getPropertyValue('--fl-analytics-gender-female')
          .trim()
      }
      return getComputedStyle(root)
        .getPropertyValue('--fl-analytics-gender-neutral')
        .trim()
    }

    /**
     * Creates a unique key for grouping links with identical source/target/value.
     * Used to detect potential duplicate keys that would cause React warnings.
     * @param {Object} link - Link object with source, target, and value properties
     * @returns {string} Composite key in format "source-target-value"
     */
    const getLinkKey = (link) => `${link.source}-${link.target}-${link.value}`

    // Group links by source-target-value to detect potential duplicates
    // Two-pass approach with Map ensures O(n) time complexity
    const linkGroups = new Map()
    const linkPositions = new Map()

    data.links?.forEach((link, index) => {
      const key = getLinkKey(link)
      if (!linkGroups.has(key)) {
        linkGroups.set(key, [])
      }
      const group = linkGroups.get(key)
      const positionInGroup = group.length
      group.push(index)
      linkPositions.set(index, positionInGroup)
    })

    return {
      ...data,
      links: (data.links || []).map((link, index) => {
        const color = getGenderColor(link.gender)

        // Check if this link has duplicates with same source-target-value
        const groupKey = getLinkKey(link)
        const group = linkGroups.get(groupKey)
        let adjustedValue = link.value

        // If there are multiple links with same source-target-value, add tiny offset
        // This prevents React duplicate key warnings while keeping visual appearance
        if (group && group.length > 1) {
          const positionInGroup = linkPositions.get(index)
          // Warn if we have too many duplicates - may need different approach
          if (group.length > 100) {
            console.warn(
              `UserJourneySankey: Found ${group.length} duplicate links for ${groupKey}`,
            )
          }
          // Add 0.0001, 0.0002, etc. - visually imperceptible but ensures unique keys
          adjustedValue = link.value + positionInGroup * DUPLICATE_LINK_OFFSET
        }

        return {
          ...link,
          value: adjustedValue,
          fill: color,
          stroke: color,
        }
      }),
    }
  }, [data])

  // Show placeholder when no data is available
  if (!data || !data.nodes || data.nodes.length === 0) {
    return (
      <div className="text-center text-muted py-4">
        <p>No user journey data available for this time period</p>
      </div>
    )
  }

  return (
    <div
      className="user-journey-sankey"
      role="img"
      aria-label="User journey flow visualization showing navigation between pages"
    >
      <ResponsiveContainer width="100%" height={500}>
        <Sankey
          data={processedData}
          node={<MyCustomNode />}
          link={{ strokeOpacity: 0.5 }}
          margin={{ top: 20, right: 160, bottom: 20, left: 20 }}
          nodePadding={50}
        >
          <Tooltip />
        </Sankey>
      </ResponsiveContainer>
    </div>
  )
}

UserJourneySankey.propTypes = {
  data: PropTypes.shape({
    nodes: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
      }),
    ),
    links: PropTypes.arrayOf(
      PropTypes.shape({
        source: PropTypes.number.isRequired,
        target: PropTypes.number.isRequired,
        value: PropTypes.number.isRequired,
      }),
    ),
  }),
}

export default UserJourneySankey
